// The Billing attribution card (SCR-585, REQ-231): the top of the org-level Usage screen in both
// shells. It reads API-580 on its own, so its loading and its failure are its own and the rest of
// the screen renders whatever it answers. States: loading · no Bedrock calls in 24 h · healthy (a
// quiet line) · reasons to act on · every call unreported · error. The words are SCR-585's
// (attribution-model.ts); an installation is its name, never its id.
//
// The meter is an SVG (the CSP forbids style attributes) sized by its viewBox, so nothing moves on
// hover; its legend carries the same counts in text for a screen reader and a phone.
import { useEffect, useState } from "preact/hooks";
import { useApi } from "../../api/context";
import { ApiError } from "../../api/errors";
import { useHost } from "../../host/host";
import { Link } from "../../primitives/link";
import { Card } from "../../primitives/page";
import { Empty, ErrorBanner, Loading } from "../../primitives/states";
import { DataTable, type Column } from "../../primitives/table";
import { ATTRIBUTION_TEXT, attributionView, type AttributionView, type BillingAttribution, type InstallationRow, type MeterSegment, type MeterSegmentKey } from "./attribution-model";
import { formatCount } from "./figures";

/** The help topic of the card; its article section is the install guide's "Billing attribution in AWS Billing". */
export const ATTRIBUTION_HELP = "usage.attribution";

const METER_WIDTH = 300;
const METER_HEIGHT = 10;

/** Each segment's colour: chart series tokens from ui.css, so both themes and CVD safety come with them. */
const SEGMENT_CLASS: Readonly<Record<MeterSegmentKey, string>> = { person: "series-1", app: "series-4", notReported: "series-other" };

function unexpected(error: unknown): ApiError {
  return error instanceof ApiError ? error : new ApiError(0, "internal", "Something went wrong.");
}

/** The three segments laid end to end in one bar; a segment of no calls draws nothing. */
function Meter({ segments, label }: { segments: readonly MeterSegment[]; label: string }) {
  let x = 0;
  const rects = segments.flatMap((segment) => {
    const width = segment.fraction * METER_WIDTH;
    const start = x;
    x += width;
    return width > 0 ? [<rect key={segment.key} class={SEGMENT_CLASS[segment.key]} x={start} y="0" width={width} height={METER_HEIGHT} />] : [];
  });
  // Sized by its own attributes (no stylesheet rule reaches it): the full width, a fixed height,
  // stretched by preserveAspectRatio. The legend sits in `.chart` for ui.css's swatch size.
  return (
    <div class="stack-sm">
      <svg width="100%" height={METER_HEIGHT} viewBox={`0 0 ${METER_WIDTH} ${METER_HEIGHT}`} preserveAspectRatio="none" role="img" aria-label={label}>
        <rect class="share-bar-track" x="0" y="0" width={METER_WIDTH} height={METER_HEIGHT} rx="3" />
        {rects}
      </svg>
      <div class="chart">
        <ul class="chart-legend">
          {segments.map((segment) => (
            <li key={segment.key}>
              <svg class="legend-swatch" viewBox="0 0 10 10" aria-hidden="true">
                <rect class={SEGMENT_CLASS[segment.key]} x="0" y="0" width="10" height="10" rx="2" />
              </svg>
              {segment.label}: {formatCount(segment.calls)}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** The rows come busiest first from the server (API-580), so the table keeps that order. */
const INSTALLATION_COLUMNS: readonly Column<InstallationRow>[] = [
  { key: "name", header: "Installation", cell: (row) => row.name },
  { key: "calls", header: "Calls", numeric: true, cell: (row) => formatCount(row.calls) },
  { key: "share", header: "Billed to a person", numeric: true, cell: (row) => row.share },
  { key: "reason", header: "Top reason", cell: (row) => row.topReason },
];

/** SCR-585's words, with each `quoted` name (an IAM action) set as code rather than shown with its backticks. */
function Words({ text }: { text: string }) {
  return <>{text.split("`").map((part, i) => (i % 2 === 1 ? <code key={i}>{part}</code> : part))}</>;
}

/** What the share leaves out, under the meter: the reasons to act on, or the quiet line. */
function Findings({ view }: { view: Extract<AttributionView, { state: "calls" }> }) {
  const lines = view.reasons.map((line) => ({ key: line.reason as string, text: `${formatCount(line.calls)} · ${line.text}` }));
  if (view.unknown) lines.push({ key: "unknown", text: view.unknown });
  if (view.notReported) lines.push({ key: "notReported", text: view.notReported });
  return (
    <>
      {view.healthy && <p class="stat-note stat-note-positive">{ATTRIBUTION_TEXT.healthy}</p>}
      {lines.length > 0 && (
        <ul class="stack-sm" aria-label="Calls not billed to a person">
          {lines.map((line) => (
            <li key={line.key}>
              <Words text={line.text} />
            </li>
          ))}
        </ul>
      )}
    </>
  );
}

function AttributionBody({ status }: { status: BillingAttribution }) {
  const host = useHost();
  const view = attributionView(status);
  if (view.state === "no-calls") return <Empty message={ATTRIBUTION_TEXT.noCalls} />;
  const guide = host.helpHref(ATTRIBUTION_HELP);
  return (
    <div class="stack">
      <p>
        <span class="stat-value">{view.share}</span> {ATTRIBUTION_TEXT.headline}
      </p>
      {status.shareBp === null && <p class="muted">{ATTRIBUTION_TEXT.noShare}</p>}
      <Meter segments={view.meter} label={`Bedrock calls in the last 24 hours: ${view.meter.map((s) => `${s.label} ${formatCount(s.calls)}`).join(", ")}`} />
      <Findings view={view} />
      <DataTable
        caption="Billing attribution by installation, last 24 hours"
        hideCaption
        keyboardScroll
        columns={INSTALLATION_COLUMNS}
        rows={view.installations}
        rowKey={(row) => row.key}
        emptyMessage={ATTRIBUTION_TEXT.noCalls}
      />
      {guide && (
        <p class="card-note">
          <Link href={guide}>{ATTRIBUTION_TEXT.setUp}</Link>
        </p>
      )}
    </div>
  );
}

/** The card, reading API-580 for the org; an error is the card's alone, with Retry. */
export function AttributionCard({ orgId }: { orgId: string }) {
  const client = useApi();
  const [status, setStatus] = useState<BillingAttribution | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let current = true;
    setStatus(null);
    setError(null);
    client
      .get("/v1/orgs/{orgId}/usage/billing-attribution", { path: { orgId } })
      .then((answer) => current && setStatus(answer))
      .catch((err: unknown) => current && setError(unexpected(err)));
    return () => {
      current = false;
    };
  }, [client, orgId, attempt]);

  return (
    <Card title={ATTRIBUTION_TEXT.title} titleId="usage-attribution" help={ATTRIBUTION_HELP}>
      {error ? (
        <ErrorBanner error={error} onRetry={() => setAttempt((n) => n + 1)} />
      ) : !status ? (
        <Loading label="Loading billing attribution…" />
      ) : (
        <AttributionBody status={status} />
      )}
    </Card>
  );
}
